import React, { useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { styled } from '@mui/system'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useExperimentSettings } from '../../hooks/useExperimentSettings'
import { ExperimentId } from '../../hooks/useExperiment'
import { colorPalette } from '../../style/colorPalette'
import { Button } from '../../components/buttons'
import { RadioButton } from '../../components/radio-button'
import { HeaderAction } from '../../components/header-action'
import { Title, Text, MultilineText } from '../../components/typography'
import Loading from '../../components/process-state-notifications/Loading'
import { addGlobalMessage, MessageLevel } from '../../../store/messages'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'

/** 設定画面用のメタ情報（i18n キー） */
type ExperimentSettingMeta = {
  nameKey: string
  descriptionKey: string
  newVariant: { nameKey: string; descriptionKey: string }
  oldVariant: { nameKey: string; descriptionKey: string }
}

/** 各実験の設定画面用メタ情報 */
const EXPERIMENT_SETTING_META: Record<ExperimentId, ExperimentSettingMeta> = {
  'wbs-status-column-layout': {
    nameKey: 'experiment.wbsStatusColumnLayout.name',
    descriptionKey: 'experiment.wbsStatusColumnLayout.description',
    newVariant: {
      nameKey: 'experiment.wbsStatusColumnLayout.newVariant.name',
      descriptionKey: 'experiment.wbsStatusColumnLayout.newVariant.description',
    },
    oldVariant: {
      nameKey: 'experiment.wbsStatusColumnLayout.oldVariant.name',
      descriptionKey: 'experiment.wbsStatusColumnLayout.oldVariant.description',
    },
  },
}

const PageContainer = styled('div')({
  height: '100%',
  overflow: 'auto',
  backgroundColor: colorPalette.monotone[1],
})

const ContentWrapper = styled('div')({
  maxWidth: 800,
  margin: '0 auto',
  padding: 24,
  display: 'flex',
  flexDirection: 'column',
  gap: 24,
})

const BackButtonArea = styled('div')({})

const SettingsCard = styled('div')({
  backgroundColor: colorPalette.monotone[0],
  borderRadius: 8,
  overflow: 'hidden',
  border: `1px solid ${colorPalette.monotone[2]}`,
})

const CardHeader = styled('div')({
  padding: '16px 24px',
  backgroundColor: colorPalette.monotone[1],
  borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
})

const CardTitleDescription = styled('div')({})

const SettingsList = styled('div')({
  '& > *:not(:last-child)': {
    borderBottom: `1px solid ${colorPalette.monotone[2]}`,
  },
})

const SettingItem = styled('div')({
  padding: '16px 24px',
})

const SettingDescriptionWrapper = styled('div')({
  marginTop: 4,
  marginBottom: 12,
})

const RadioGroup = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  paddingLeft: 4,
})

const RadioOption = styled('div')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  cursor: 'pointer',
})

const RadioTextWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
})

export const Preferences: React.FC = () => {
  const history = useHistory()
  const dispatch = useDispatch()
  const intl = useIntl()
  const { settings, updateSetting, save, isDirty, isSaving } =
    useExperimentSettings()

  const onBack = useCallback(() => {
    history.goBack()
  }, [history])

  const onSave = useCallback(async () => {
    if (!isDirty || isSaving) return
    await save()
    dispatch(
      addGlobalMessage({
        type: MessageLevel.SUCCESS,
        title: intl.formatMessage({ id: 'preferences.saveSuccess' }),
      })
    )
  }, [save, dispatch, intl, isDirty, isSaving])

  // Ctrl+S で保存
  useKeyBind(
    [
      {
        key: KEY_SAVE,
        fn: onSave,
        stopDefaultBehavior: true,
      },
    ],
    [onSave]
  )

  return (
    <PageContainer>
      <Loading isLoading={isSaving} />
      <HeaderAction
        title={intl.formatMessage({ id: 'unsavedChanges' })}
        buttonLabel={intl.formatMessage({ id: 'save' })}
        buttonSubLabel={intl.formatMessage({ id: 'saveShortcut' })}
        onButtonClick={onSave}
        open={isDirty && !isSaving}
      />
      <ContentWrapper>
        <BackButtonArea>
          <Button
            onClick={onBack}
            colorPattern="monotone"
            variant="outlined"
            size="m"
          >
            <ArrowBackIcon sx={{ fontSize: 16 }} />
            {intl.formatMessage({ id: 'back' })}
          </Button>
        </BackButtonArea>

        <SettingsCard>
          <CardHeader>
            <Title variant="s" as="h2">
              {intl.formatMessage({ id: 'preferences.card.title' })}
            </Title>
            <CardTitleDescription>
              <MultilineText color={colorPalette.monotone[4]}>
                {intl.formatMessage({ id: 'preferences.card.description' })}
              </MultilineText>
            </CardTitleDescription>
          </CardHeader>

          <SettingsList>
            {settings.map(setting => {
              const meta = EXPERIMENT_SETTING_META[setting.experimentId]
              return (
                <SettingItem key={setting.experimentId}>
                  <Title variant="s" as="h3">
                    {intl.formatMessage({ id: meta.nameKey })}
                  </Title>
                  <SettingDescriptionWrapper>
                    <MultilineText color={colorPalette.monotone[4]}>
                      {intl.formatMessage({ id: meta.descriptionKey })}
                    </MultilineText>
                  </SettingDescriptionWrapper>
                  <RadioGroup>
                    <RadioOption
                      onClick={() => updateSetting(setting.experimentId, true)}
                    >
                      <RadioButton
                        name={setting.experimentId}
                        checked={setting.useNew}
                        onChange={() =>
                          updateSetting(setting.experimentId, true)
                        }
                      />
                      <RadioTextWrapper>
                        <Text>
                          {intl.formatMessage({ id: meta.newVariant.nameKey })}
                        </Text>
                        <Text variant="2s" color={colorPalette.monotone[4]}>
                          {intl.formatMessage({
                            id: meta.newVariant.descriptionKey,
                          })}
                        </Text>
                      </RadioTextWrapper>
                    </RadioOption>
                    <RadioOption
                      onClick={() => updateSetting(setting.experimentId, false)}
                    >
                      <RadioButton
                        name={setting.experimentId}
                        checked={!setting.useNew}
                        onChange={() =>
                          updateSetting(setting.experimentId, false)
                        }
                      />
                      <RadioTextWrapper>
                        <Text>
                          {intl.formatMessage({ id: meta.oldVariant.nameKey })}
                        </Text>
                        <Text variant="2s" color={colorPalette.monotone[4]}>
                          {intl.formatMessage({
                            id: meta.oldVariant.descriptionKey,
                          })}
                        </Text>
                      </RadioTextWrapper>
                    </RadioOption>
                  </RadioGroup>
                </SettingItem>
              )
            })}
          </SettingsList>
        </SettingsCard>
      </ContentWrapper>
    </PageContainer>
  )
}

export default Preferences
